import React, { useEffect, useState } from 'react';
import api from '../../../services/api';
import { Link } from 'react-router-dom';
import { Table, Button, Tag, Alert, Card, Row, Col, Statistic, Progress, Typography, Space, Badge, Avatar, List, Timeline, Divider } from 'antd';
import { 
    UserOutlined, 
    ShopOutlined, 
    ShoppingCartOutlined, 
    DollarOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    ExclamationCircleOutlined,
    StarOutlined,
    EyeOutlined,
    EditOutlined,
    DeleteOutlined
} from '@ant-design/icons';
import { getStatusColor, formatCurrency } from '../../../utils/ui';

function AdminDashboard() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [vendors, setVendors] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [verifyingId, setVerifyingId] = useState(null);
    const [stats, setStats] = useState({
        totalUsers: 0,
        totalVendors: 0,
        totalBookings: 0,
        totalRevenue: 0,
        pendingVendors: 0,
        pendingMenus: 0,
        monthlyGrowth: 0,
        conversionRate: 0
    });

    useEffect(() => {
        const load = async () => {
            try {
                const [vendorsRes, bookingsRes, usersRes, leadsRes] = await Promise.all([
                    api.adminListVendors({ per_page: 20, verified: 0 }),
                    api.adminListBookings({ per_page: 10 }),
                    api.adminListUsers({ per_page: 1 }),
                    api.adminGetLeads({ per_page: 1 }),
                ]);
                setVendors(vendorsRes.data.data || []);
                setBookings(bookingsRes.data.data || []);

                const totalUsers = usersRes?.data?.total ?? usersRes?.data?.meta?.total ?? usersRes?.data?.data?.length ?? 0;
                const totalVendors = vendorsRes?.data?.total ?? vendorsRes?.data?.meta?.total ?? vendorsRes?.data?.data?.length ?? 0;
                const totalBookings = bookingsRes?.data?.total ?? bookingsRes?.data?.meta?.total ?? bookingsRes?.data?.data?.length ?? 0;
                const pendingVendors = vendorsRes?.data?.data?.length || 0;

                setStats((prev) => ({
                    ...prev,
                    totalUsers,
                    totalVendors,
                    totalBookings,
                    // Revenue and rates require dedicated endpoints; keep zeros until backend provides.
                    totalRevenue: prev.totalRevenue || 0,
                    pendingVendors,
                }));
            } catch (e) {
                setError('Failed to load admin data');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const verifyVendor = async (id) => {
        try {
            setVerifyingId(id);
            await api.adminVerifyVendor(id);
            const vendorsRes = await api.adminListVendors({ per_page: 20, verified: 0 });
            setVendors(vendorsRes.data.data || []);
        } catch (e) {
            setError('Failed to verify vendor');
        } finally {
            setVerifyingId(null);
        }
    };

    if (loading) {
        return (
            <div className="min-h-[50vh] flex items-center justify-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-600"></div>
            </div>
        );
    }

    const vendorColumns = [
        { title: 'Name', dataIndex: ['business_name'], key: 'name' },
        { title: 'City', dataIndex: ['city', 'name'], key: 'city' },
        { title: 'Rating', key: 'rating', render: (_, v) => `${v.rating} (${v.total_reviews})` },
        { title: 'Min Order', dataIndex: ['min_order_amount'], key: 'min_order', render: (amt) => formatCurrency(amt) },
        { title: 'Status', key: 'status', render: (_, v) => (
            <Tag color={getStatusColor(v.is_verified ? 'success' : 'pending')}>{v.is_verified ? 'Verified' : 'Pending'}</Tag>
        ) },
        { title: '', key: 'action', align: 'right', render: (_, v) => (
            !v.is_verified ? (
                <Button type="primary" loading={verifyingId === v.id} onClick={() => verifyVendor(v.id)}>Verify</Button>
            ) : null
        )},
    ];

    const bookingColumns = [
        { title: 'Number', dataIndex: ['booking_number'], key: 'number' },
        { title: 'Customer', dataIndex: ['user', 'name'], key: 'customer' },
        { title: 'Vendor', dataIndex: ['vendor', 'business_name'], key: 'vendor' },
        { title: 'Amount', dataIndex: ['total_amount'], key: 'amount', render: (amt) => formatCurrency(amt) },
        { title: 'Status', dataIndex: ['status'], key: 'status', render: (status) => (
            <Tag color={getStatusColor(status)}>{status}</Tag>
        ) },
        { title: 'Payment', key: 'payment', render: (_, b) => (
            b.order?.payment_status ? (
                <Tag color={b.order.payment_status === 'paid' ? 'green' : 'default'}>{b.order.payment_status}</Tag>
            ) : '-'
        ) },
    ];

    return (
        <div>
            {error && (<Alert type="error" message={error} className="mb-6" />)}

                {/* Key Metrics */}
                <Row gutter={[16, 16]} className="mb-6">
                    <Col xs={24} sm={12} lg={6}>
                        <Card className="card-compact">
                            <Statistic
                                title="Total Users"
                                value={stats.totalUsers}
                                prefix={<UserOutlined />}
                                valueStyle={{ color: '#3f8600' }}
                                suffix={<ArrowUpOutlined style={{ color: '#3f8600' }} />}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card className="card-compact">
                            <Statistic
                                title="Active Vendors"
                                value={stats.totalVendors}
                                prefix={<ShopOutlined />}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card className="card-compact">
                            <Statistic
                                title="Total Bookings"
                                value={stats.totalBookings}
                                prefix={<ShoppingCartOutlined />}
                                valueStyle={{ color: '#722ed1' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card className="card-compact">
                            <Statistic
                                title="Total Revenue"
                                value={stats.totalRevenue}
                                prefix={<DollarOutlined />}
                                valueStyle={{ color: '#f5222d' }}
                                formatter={(value) => `₹${value.toLocaleString()}`}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Performance Metrics */}
                <Row gutter={[16, 16]} className="mb-6">
                    <Col xs={24} lg={12}>
                        <Card title="Monthly Growth" extra={<ArrowUpOutlined style={{ color: '#52c41a' }} />}>
                            <div className="text-center">
                                <Progress 
                                    type="circle" 
                                    percent={stats.monthlyGrowth} 
                                    format={() => `${stats.monthlyGrowth}%`}
                                    strokeColor="#52c41a"
                                />
                                <Typography.Text type="secondary" className="block mt-2">
                                    User growth this month
                                </Typography.Text>
                            </div>
                        </Card>
                    </Col>
                    <Col xs={24} lg={12}>
                        <Card title="Conversion Rate" extra={<CheckCircleOutlined style={{ color: '#1890ff' }} />}>
                            <div className="text-center">
                                <Progress 
                                    type="circle" 
                                    percent={stats.conversionRate} 
                                    format={() => `${stats.conversionRate}%`}
                                    strokeColor="#1890ff"
                                />
                                <Typography.Text type="secondary" className="block mt-2">
                                    Visitor to booking conversion
                                </Typography.Text>
                </div>
                        </Card>
                    </Col>
                </Row>

                {/* Main Content Grid */}
                <Row gutter={[16, 16]}>
                    {/* Pending Vendors */}
                    <Col xs={24} lg={12}>
                        <Card 
                            title={
                                <Space>
                                    <ShopOutlined />
                                    Pending Vendor Approvals
                                    <Badge count={vendors.length} />
                                </Space>
                            }
                            extra={<Link to="/admin/vendors">View All</Link>}
                        >
                            <Table 
                                columns={vendorColumns} 
                                dataSource={vendors.slice(0, 5)} 
                                rowKey="id" 
                                pagination={false} 
                                size="small"
                                scroll={{ x: true }}
                            />
                        </Card>
                    </Col>

                    {/* Recent Bookings */}
                    <Col xs={24} lg={12}>
                        <Card 
                            title={
                                <Space>
                                    <ShoppingCartOutlined />
                                    Recent Bookings
                                </Space>
                            }
                            extra={<Link to="/admin/bookings">View All</Link>}
                        >
                            <Table 
                                columns={bookingColumns} 
                                dataSource={bookings.slice(0, 5)} 
                                rowKey="id" 
                                pagination={false} 
                                size="small"
                                scroll={{ x: true }}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Quick Actions */}
                <Row gutter={[16, 16]} className="mt-6">
                    <Col xs={24} sm={12} lg={6}>
                        <Card hoverable className="text-center h-full">
                            <div className="text-4xl mb-3">🏙️</div>
                            <Typography.Title level={4} className="!mb-2">Cities</Typography.Title>
                            <Typography.Paragraph type="secondary" className="!mb-4">
                                Manage serviceable cities
                            </Typography.Paragraph>
                            <Link to="/admin/cities">
                                <Button type="primary" block>Manage Cities</Button>
                            </Link>
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card hoverable className="text-center h-full">
                            <div className="text-4xl mb-3">📂</div>
                            <Typography.Title level={4} className="!mb-2">Categories</Typography.Title>
                            <Typography.Paragraph type="secondary" className="!mb-4">
                                Control menu categories
                            </Typography.Paragraph>
                            <Link to="/admin/categories">
                                <Button type="primary" block>Manage Categories</Button>
                            </Link>
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card hoverable className="text-center h-full">
                            <div className="text-4xl mb-3">🍽️</div>
                            <Typography.Title level={4} className="!mb-2">Menus</Typography.Title>
                            <Typography.Paragraph type="secondary" className="!mb-4">
                                Moderate vendor menus
                            </Typography.Paragraph>
                            <Link to="/admin/menus">
                                <Button type="primary" block>Moderate Menus</Button>
                            </Link>
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card hoverable className="text-center h-full">
                            <div className="text-4xl mb-3">📦</div>
                            <Typography.Title level={4} className="!mb-2">Packages</Typography.Title>
                            <Typography.Paragraph type="secondary" className="!mb-4">
                                Create preset packages
                            </Typography.Paragraph>
                            <Link to="/admin/packages">
                                <Button type="primary" block>Manage Packages</Button>
                            </Link>
                        </Card>
                    </Col>
                </Row>

                {/* Additional Admin Tools */}
                <Row gutter={[16, 16]} className="mt-6">
                    <Col xs={24} sm={12} lg={6}>
                        <Card hoverable className="text-center h-full">
                            <div className="text-4xl mb-3">👥</div>
                            <Typography.Title level={4} className="!mb-2">Users</Typography.Title>
                            <Typography.Paragraph type="secondary" className="!mb-4">
                                Manage user accounts
                            </Typography.Paragraph>
                            <Link to="/admin/users">
                                <Button block>Manage Users</Button>
                            </Link>
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card hoverable className="text-center h-full">
                            <div className="text-4xl mb-3">📊</div>
                            <Typography.Title level={4} className="!mb-2">Analytics</Typography.Title>
                            <Typography.Paragraph type="secondary" className="!mb-4">
                                View detailed reports
                            </Typography.Paragraph>
                            <Link to="/admin/analytics">
                                <Button block>View Analytics</Button>
                            </Link>
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card hoverable className="text-center h-full">
                            <div className="text-4xl mb-3">🎨</div>
                            <Typography.Title level={4} className="!mb-2">Content</Typography.Title>
                            <Typography.Paragraph type="secondary" className="!mb-4">
                                Manage banners & pages
                            </Typography.Paragraph>
                            <Link to="/admin/content">
                                <Button block>Manage Content</Button>
                            </Link>
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card hoverable className="text-center h-full">
                            <div className="text-4xl mb-3">📧</div>
                            <Typography.Title level={4} className="!mb-2">Email Config</Typography.Title>
                            <Typography.Paragraph type="secondary" className="!mb-4">
                                Configure email settings
                            </Typography.Paragraph>
                            <Link to="/admin/email-configuration">
                                <Button block>Email Settings</Button>
                            </Link>
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card hoverable className="text-center h-full">
                            <div className="text-4xl mb-3">⚙️</div>
                            <Typography.Title level={4} className="!mb-2">Settings</Typography.Title>
                            <Typography.Paragraph type="secondary" className="!mb-4">
                                Platform configuration
                            </Typography.Paragraph>
                            <Link to="/admin/settings">
                                <Button block>Platform Settings</Button>
                            </Link>
                        </Card>
                    </Col>
                </Row>

                {/* System Management Tools */}
                <Row gutter={[16, 16]} className="mt-6">
                    <Col xs={24} sm={12} lg={6}>
                        <Card hoverable className="text-center h-full">
                            <div className="text-4xl mb-3">🔌</div>
                            <Typography.Title level={4} className="!mb-2">Integrations</Typography.Title>
                            <Typography.Paragraph type="secondary" className="!mb-4">
                                Manage third-party services
                            </Typography.Paragraph>
                            <Link to="/admin/integrations">
                                <Button block>Manage Integrations</Button>
                            </Link>
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card hoverable className="text-center h-full">
                            <div className="text-4xl mb-3">🔔</div>
                            <Typography.Title level={4} className="!mb-2">Notifications</Typography.Title>
                            <Typography.Paragraph type="secondary" className="!mb-4">
                                Send emails, SMS, push notifications
                            </Typography.Paragraph>
                            <Link to="/admin/notifications">
                                <Button block>Manage Notifications</Button>
                            </Link>
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card hoverable className="text-center h-full">
                            <div className="text-4xl mb-3">🐛</div>
                            <Typography.Title level={4} className="!mb-2">System Logs</Typography.Title>
                            <Typography.Paragraph type="secondary" className="!mb-4">
                                Monitor system activity & errors
                            </Typography.Paragraph>
                            <Link to="/admin/system-logs">
                                <Button block>View Logs</Button>
                            </Link>
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card hoverable className="text-center h-full">
                            <div className="text-4xl mb-3">💾</div>
                            <Typography.Title level={4} className="!mb-2">Backup & Restore</Typography.Title>
                            <Typography.Paragraph type="secondary" className="!mb-4">
                                Database and file backups
                            </Typography.Paragraph>
                            <Link to="/admin/backups">
                                <Button block>Manage Backups</Button>
                            </Link>
                        </Card>
                    </Col>
                </Row>
        </div>
    );
}

export default AdminDashboard;
